import { io, Socket } from "socket.io-client";
import { reconcileElements } from "@excalidraw/excalidraw";

import type {
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";
import type { OrderedExcalidrawElement } from "@excalidraw/element/types";
import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";

export interface Collaborator {
  id: string;
  name: string;
  picture: string;
}

type LiveSyncListener = {
  onCollaboratorsChanged?: (collaborators: Collaborator[]) => void;
};

export class LiveSync {
  private socket: Socket | null = null;
  private drawingId: string | null = null;
  private excalidrawAPI: ExcalidrawImperativeAPI | null = null;
  private listener: LiveSyncListener = {};
  private isApplyingRemote = false;

  connect(excalidrawAPI: ExcalidrawImperativeAPI, listener: LiveSyncListener) {
    this.excalidrawAPI = excalidrawAPI;
    this.listener = listener;

    if (this.socket?.connected) {
      return;
    }

    this.socket = io({
      path: "/api/ws",
      withCredentials: true,
      transports: ["websocket", "polling"],
    });

    this.socket.on("connect", () => {
      console.log("[LiveSync] Connected");
      if (this.drawingId) {
        this.socket!.emit("join-drawing", this.drawingId);
      }
    });

    this.socket.on("scene-update", (data: { elements: any; from: { id: string; name: string } }) => {
      if (!this.excalidrawAPI) {
        return;
      }
      console.log(`[LiveSync] Received update from ${data.from.name}`);
      this.isApplyingRemote = true;
      try {
        const localElements = this.excalidrawAPI.getSceneElementsIncludingDeleted();
        const reconciledElements = reconcileElements(
          localElements as readonly OrderedExcalidrawElement[],
          data.elements as RemoteExcalidrawElement[],
          this.excalidrawAPI.getAppState(),
        );
        this.excalidrawAPI.updateScene({
          elements: reconciledElements,
        });
      } finally {
        // Small delay to avoid echo
        setTimeout(() => {
          this.isApplyingRemote = false;
        }, 100);
      }
    });

    this.socket.on("collaborators-changed", (data: { collaborators: Collaborator[] }) => {
      this.listener.onCollaboratorsChanged?.(data.collaborators);
    });

    this.socket.on("disconnect", () => {
      console.log("[LiveSync] Disconnected");
    });
  }

  joinDrawing(drawingId: string) {
    this.drawingId = drawingId;
    if (this.socket?.connected) {
      this.socket.emit("join-drawing", drawingId);
    }
  }

  broadcastElements(elements: readonly OrderedExcalidrawElement[]) {
    if (!this.socket?.connected || !this.drawingId || this.isApplyingRemote) {
      return;
    }
    this.socket.emit("scene-update", {
      drawingId: this.drawingId,
      elements,
    });
  }

  broadcastCursor(x: number, y: number) {
    if (!this.socket?.connected || !this.drawingId) {
      return;
    }
    this.socket.emit("cursor-update", {
      drawingId: this.drawingId,
      x,
      y,
    });
  }

  disconnect() {
    this.socket?.disconnect();
    this.socket = null;
    this.drawingId = null;
  }
}
